/*
  Timeline, wave 3 (screen 10) — pure helpers. No React. Unit-tested in project-detail-w3b.test.js.

  - buildAutoPlan()      #3 Auto-plan: the open delivery stages (one after another) and the open main execution
                         tasks (after their template dependencies) get dates from the plan start, in working days
                         of the chosen work week, with template durations; a buffer % pushes the handover.
                         Items that already have dates stay where they are and constrain what follows them —
                         except a saved item that slipped (still open, its end before the plan start) and what
                         it pushes (a saved item that would now start before its re-planned predecessor ends):
                         those are re-planned from the plan start, keeping their length (a recovery plan), so
                         Apply plan can move dates that were applied before. `replan: true` (Re-plan all open
                         items) proposes every open item afresh, ignoring saved dates.
  - criticalPath()       #8 the chain of items that decides the end date (no slack); its arrows are red
  - forecastSlips()      #8 saved dates: a late item, or one whose predecessor ends after it was due to start,
                         pushes everything after it — the forecast end and handover move with it
  - billingMilestones()  #4 invoices (received / submitted / scheduled), VO approvals and retention as diamonds
  - lookaheadRows() / lookaheadHtml()  #7 what is planned in the next three weeks, printable (Save as PDF)

  Dates are 'YYYY-MM-DD' calendar days (lib/gantt day numbers, never shifted by a time zone).
*/
import { addWorkingDays, dayToIso, toDay, workingDaysBetween } from '@/lib/gantt';
import { formatDate, formatNumber } from '@/lib/format';
import { decisionStep } from './variationTrailModel';

/** Admin › Settings work weeks (backend app_settings.WORK_WEEKS); `weekend` in JS getDay() numbers. */
export const PLAN_WORK_WEEKS = [
  { key: 'mon_fri', label: 'Mon–Fri', weekend: [6, 0] },
  { key: 'mon_sat', label: 'Mon–Sat', weekend: [0] },
  { key: 'sun_thu', label: 'Sun–Thu', weekend: [5, 6] },
];
export const PLAN_BUFFERS = [0, 10, 20, 30];
export const weekendFor = (key) => (PLAN_WORK_WEEKS.find((week) => week.key === key) || PLAN_WORK_WEEKS[1]).weekend;

/** Working days a delivery stage takes when the stage template gives none (by stage name). */
export const STAGE_PLAN_DAYS = {
  'material submittal': 5,
  'technical submittal': 5,
  'boq & design': 5,
  'lpo/loi': 3,
  'shop drawings': 10,
  schematics: 5,
  'handing over documents': 5,
};
export const DEFAULT_STAGE_DAYS = 5;
/** Same table as backend/project_plan.py EXECUTION_PLAN_DEFAULTS (used when an old template has none). */
export const EXECUTION_PLAN_DEFAULTS = {
  'review-submittal': [3, []],
  'shop-drawings': [10, ['review-submittal']],
  'panel-design': [5, ['shop-drawings']],
  'material-availability': [5, ['review-submittal']],
  'panel-manufacturing': [10, ['panel-design', 'material-availability']],
  'cable-pulling': [10, ['shop-drawings']],
  'cable-testing': [5, ['cable-pulling']],
  'panel-termination': [5, ['panel-manufacturing', 'cable-testing']],
  'interfaces-installation': [10, ['panel-termination']],
  'pre-programming': [5, ['panel-design']],
  'soo-programming': [5, ['pre-programming', 'interfaces-installation']],
  'as-built-drawings': [5, ['soo-programming']],
  graphics: [5, ['soo-programming']],
  'handing-over-documents': [10, ['as-built-drawings', 'graphics']],
};

const text = (value) => String(value ?? '').trim();
const DONE_STAGE = new Set(['approved', 'completed']);
const CLOSED_TASK = new Set(['Done', 'Not required']);
const ONGOING_STAGES = new Set(['variations']);

export const isStageDone = (stage) => DONE_STAGE.has(text(stage?.status).toLowerCase());
export const isOngoingStage = (stage) => ONGOING_STAGES.has(text(stage?.name).toLowerCase());
export const isTaskClosed = (task) => CLOSED_TASK.has(text(task?.status));
export const stagePlanDays = (stage) => STAGE_PLAN_DAYS[text(stage?.name).toLowerCase()] || DEFAULT_STAGE_DAYS;

/** The template id of a main execution task ('main::panel-design' → 'panel-design'). */
export const templateIdOf = (task) => {
  const key = text(task?.task_key);
  return key.startsWith('main::') ? key.slice(6) : '';
};

/** Template item by id with duration_days / depends_on (filled from the defaults when missing). */
export function templatePlanIndex(template = []) {
  const index = new Map();
  (template || []).forEach((item) => {
    if (!item?.id) return;
    const [days, deps] = EXECUTION_PLAN_DEFAULTS[item.id] || [5, []];
    index.set(item.id, {
      days: Number(item.duration_days) > 0 ? Math.round(Number(item.duration_days)) : days,
      deps: Array.isArray(item.depends_on) ? item.depends_on : deps,
    });
  });
  return index;
}

const nextWorkingDay = (iso, weekend) => {
  const day = toDay(iso);
  return day === null ? null : addWorkingDays(dayToIso(day + 1), 1, { weekend });
};
const maxIso = (list) => list.filter(Boolean).sort().pop() || null;

/** The dates a main task already has (its own, else the span of its sub-items). */
export function taskDates(task, tree) {
  if (task?.start_date || task?.due_date) {
    const start = text(task.start_date) || text(task.due_date);
    const end = text(task.due_date) || text(task.start_date);
    return start <= end ? { start, end } : { start: end, end: start };
  }
  const children = tree?.children?.get?.(task?.id) || [];
  const starts = [];
  const ends = [];
  const walk = (list) => list.forEach((child) => {
    if (child.start_date || child.due_date) {
      starts.push(text(child.start_date) || text(child.due_date));
      ends.push(text(child.due_date) || text(child.start_date));
    }
    walk(tree?.children?.get?.(child.id) || []);
  });
  walk(children);
  if (!starts.length) return null;
  return { start: starts.sort()[0], end: ends.sort().pop() };
}

/**
 * Auto-plan (#3). Returns { items, edges, handover, lastEnd, bufferDays, workingDays, proposedCount, newCount,
 * replannedCount, savedOpenCount, replan, replanAvailable }.
 * items: [{ key, kind: 'stage'|'execution', id, label, start, end, days, proposed,
 *           replanned?: 'late'|'pushed'|'all', was?: { start, end } }]
 * edges: [{ from, to }] (item keys, finish-to-start).
 * `replan`: true = every open item is proposed afresh (saved dates ignored); false = only slipped items and what
 * they push are re-planned. Left undefined by a caller that offers no "Re-plan all" switch (replanAvailable false).
 */
export function buildAutoPlan({ stages = [], mainTasks = [], template = [], tree = null, start, weekend = [0], bufferPercent = 0, durations = {}, replan } = {}) {
  const planStart = addWorkingDays(start, 1, { weekend });
  const empty = {
    items: [], edges: [], handover: null, lastEnd: null, bufferDays: 0, workingDays: 0, proposedCount: 0,
    newCount: 0, replannedCount: 0, savedOpenCount: 0, replan: replan === true, replanAvailable: false,
  };
  if (!planStart) return empty;
  const items = [];
  const edges = [];
  const replanAll = replan === true;
  const daysFor = (key, fallback) => {
    const value = Number(durations[key]);
    return value > 0 ? Math.round(value) : fallback;
  };
  /** Why a saved item gets new dates (null: it keeps them). `before` = its predecessors in this plan; only a
   *  re-planned one pushes it (a predecessor that merely had no dates yet does not move saved dates). */
  const replanReason = (saved, before) => {
    if (replanAll) return 'all';
    if (saved.end < planStart) return 'late';
    const after = maxIso(before.filter((item) => item.replanned).map((item) => item.end));
    if (after && nextWorkingDay(after, weekend) > saved.start) return 'pushed';
    return null;
  };
  const keptLength = (saved) => Math.max(1, workingDaysBetween(saved.start, saved.end, { weekend }));
  let savedOpenCount = 0;

  // Delivery stages: the open ones, one after another (finish-to-start), in pipeline order.
  let previous = null;
  stages
    .filter((stage) => !isStageDone(stage) && !isOngoingStage(stage))
    .forEach((stage) => {
      const key = `stage:${stage.id}`;
      const saved = stage.planned_start && stage.planned_end ? { start: stage.planned_start, end: stage.planned_end } : null;
      if (saved) savedOpenCount += 1;
      const reason = saved ? replanReason(saved, previous ? [previous] : []) : null;
      let item;
      if (saved && !reason) {
        item = { key, kind: 'stage', id: stage.id, label: stage.name, start: saved.start, end: saved.end, days: workingDaysBetween(saved.start, saved.end, { weekend }), proposed: false };
      } else {
        const begin = previous ? maxIso([planStart, nextWorkingDay(previous.end, weekend)]) : planStart;
        const days = saved && reason !== 'all' ? keptLength(saved) : daysFor(key, stagePlanDays(stage));
        item = { key, kind: 'stage', id: stage.id, label: stage.name, start: begin, end: addWorkingDays(begin, days, { weekend }), days, proposed: true };
        if (saved) Object.assign(item, { replanned: reason, was: saved });
      }
      if (previous) edges.push({ from: previous.key, to: key });
      items.push(item);
      previous = item;
    });

  // Execution: the open main tasks, after their template dependencies (closed ones are finished already).
  const plan = templatePlanIndex(template);
  const byTemplate = new Map(mainTasks.map((task) => [templateIdOf(task), task]));
  const placed = new Map();
  const visiting = new Set();
  const place = (task) => {
    const templateId = templateIdOf(task);
    const key = `task:${task.id}`;
    if (placed.has(key)) return placed.get(key);
    if (isTaskClosed(task)) return null;
    if (visiting.has(key)) return null; // a cycle in a hand-edited template: ignore the closing edge
    visiting.add(key);
    const info = plan.get(templateId) || { days: 5, deps: [] };
    const depItems = (info.deps || [])
      .map((depId) => byTemplate.get(depId))
      .filter(Boolean)
      .map((depTask) => place(depTask))
      .filter(Boolean);
    visiting.delete(key);
    const saved = taskDates(task, tree);
    if (saved) savedOpenCount += 1;
    const reason = saved ? replanReason(saved, depItems) : null;
    let item;
    if (saved && !reason) {
      item = { key, kind: 'execution', id: task.id, label: text(task.title) || 'Task', start: saved.start, end: saved.end, days: workingDaysBetween(saved.start, saved.end, { weekend }), proposed: false };
    } else {
      const after = maxIso(depItems.map((dep) => dep.end));
      const begin = after ? maxIso([planStart, nextWorkingDay(after, weekend)]) : planStart;
      const days = saved && reason !== 'all' ? keptLength(saved) : daysFor(key, info.days);
      item = { key, kind: 'execution', id: task.id, label: text(task.title) || 'Task', start: begin, end: addWorkingDays(begin, days, { weekend }), days, proposed: true };
      if (saved) Object.assign(item, { replanned: reason, was: saved });
    }
    depItems.forEach((dep) => edges.push({ from: dep.key, to: key }));
    placed.set(key, item);
    items.push(item);
    return item;
  };
  mainTasks.forEach((task) => place(task));

  const lastEnd = maxIso(items.map((item) => item.end));
  const workingDays = lastEnd ? workingDaysBetween(planStart, lastEnd, { weekend }) : 0;
  const bufferDays = Math.ceil((workingDays * Math.max(0, Number(bufferPercent) || 0)) / 100);
  const handover = lastEnd ? (bufferDays ? addWorkingDays(nextWorkingDay(lastEnd, weekend), bufferDays, { weekend }) : lastEnd) : null;
  return {
    start: planStart,
    items,
    edges,
    lastEnd,
    bufferDays,
    workingDays: workingDays + bufferDays,
    handover,
    proposedCount: items.filter((item) => item.proposed).length,
    newCount: items.filter((item) => item.proposed && !item.replanned).length,
    replannedCount: items.filter((item) => item.replanned).length,
    savedOpenCount,
    replan: replanAll,
    // the caller passes `replan` (true / false) only when it offers the "Re-plan all open items" switch
    replanAvailable: replan !== undefined && savedOpenCount > 0,
  };
}

/**
 * Critical path (#8): the items with no slack — those ending on the last day, and, going back, each predecessor
 * whose end is what fixes the next one's start. Returns { items: Set(key), edges: Set('from>to') }.
 */
export function criticalPath(items = [], edges = [], weekend = [0]) {
  const byKey = new Map(items.map((item) => [item.key, item]));
  const last = maxIso(items.map((item) => item.end));
  const critical = new Set(items.filter((item) => item.end === last).map((item) => item.key));
  const criticalEdges = new Set();
  const queue = [...critical];
  while (queue.length) {
    const key = queue.shift();
    const item = byKey.get(key);
    edges.filter((edge) => edge.to === key).forEach((edge) => {
      const from = byKey.get(edge.from);
      if (!from || !item) return;
      // tight: the item starts on the first working day after its predecessor ends (or earlier — a clash)
      if (nextWorkingDay(from.end, weekend) >= item.start) {
        criticalEdges.add(`${edge.from}>${edge.to}`);
        if (!critical.has(edge.from)) {
          critical.add(edge.from);
          queue.push(edge.from);
        }
      }
    });
  }
  return { items: critical, edges: criticalEdges };
}

/**
 * Saved dates (#8): a late open item (its end before today) runs at least until today, and an item starts no
 * earlier than the working day after its predecessors end. Returns Map(key → { start, end, slip }) for the items
 * that move, plus the forecast last day: { moved, lastEnd }.
 */
export function forecastSlips(items = [], edges = [], { weekend = [0], today, closed = new Set() } = {}) {
  const byKey = new Map(items.map((item) => [item.key, { ...item }]));
  const preds = new Map();
  edges.forEach((edge) => {
    if (!preds.has(edge.to)) preds.set(edge.to, []);
    preds.get(edge.to).push(edge.from);
  });
  const order = [];
  const seen = new Set();
  const visit = (key, stack = new Set()) => {
    if (seen.has(key) || stack.has(key)) return;
    stack.add(key);
    (preds.get(key) || []).forEach((pred) => visit(pred, stack));
    stack.delete(key);
    seen.add(key);
    order.push(key);
  };
  items.forEach((item) => visit(item.key));
  const moved = new Map();
  order.forEach((key) => {
    const item = byKey.get(key);
    if (!item || closed.has(key)) return;
    const duration = Math.max(1, workingDaysBetween(item.start, item.end, { weekend }));
    let start = item.start;
    (preds.get(key) || []).forEach((predKey) => {
      const pred = byKey.get(predKey);
      if (!pred || closed.has(predKey)) return;
      const earliest = nextWorkingDay(pred.end, weekend);
      if (earliest && earliest > start) start = earliest;
    });
    let end = start === item.start ? item.end : addWorkingDays(start, duration, { weekend });
    if (today && end < today && !item.proposed) end = today; // not finished yet: it runs until today at least
    if (start !== item.start || end !== item.end) {
      const slip = Math.max(0, workingDaysBetween(item.end, end, { weekend }) - 1);
      moved.set(key, { start, end, slip });
      item.start = start;
      item.end = end;
    }
  });
  return { moved, lastEnd: maxIso([...byKey.values()].map((item) => item.end)) };
}

const SHORT_TYPES = [
  [/advance/i, 'Advance'],
  [/deliver/i, 'Delivery'],
  [/install/i, 'Installation'],
  [/retention/i, 'Retention'],
  [/t\s*&\s*c|testing|commission/i, 'T&C'],
];
export const shortInvoiceType = (type) => {
  const found = SHORT_TYPES.find(([pattern]) => pattern.test(text(type)));
  return found ? found[1] : (text(type).split(/\s+/)[0] || 'Invoice');
};

/** The day an invoice counts on the timeline: paid, else submitted, else its due / created day. */
export function invoiceDay(invoice = {}) {
  return text(invoice.paid_at).slice(0, 10) || text(invoice.submitted_at).slice(0, 10)
    || (text(invoice.status) === 'Not Started' ? text(invoice.due_date).slice(0, 10) : text(invoice.created_at).slice(0, 10)) || '';
}

const INVOICE_TONES = { 'Payment Received': 'navy', Submitted: 'amber' };

/**
 * Milestones lane (#4): [{ id, date, label, tone, mono?, kind: 'invoice'|'variation'|'retention' }] plus counts.
 * Retention without a date of its own sits on the handover (planned, or the proposed one).
 */
export function billingMilestones({ invoices = [], variations = [], handover = null } = {}) {
  const list = [];
  let retention = null;
  (invoices || []).forEach((invoice) => {
    const type = shortInvoiceType(invoice.invoice_type);
    if (type === 'Retention' && text(invoice.status || 'Not Started') === 'Not Started' && !text(invoice.due_date)) {
      retention = { ...(retention || {}), amount: (retention?.amount || 0) + Math.abs(Number(invoice.amount) || 0) };
      return;
    }
    const date = invoiceDay(invoice);
    if (!date) return;
    list.push({
      id: `inv-${invoice.id}`,
      date,
      label: `${text(invoice.reference) || '—'} · ${type}`,
      tone: INVOICE_TONES[text(invoice.status)] || 'gray',
      kind: 'invoice',
    });
  });
  (variations || []).filter((variation) => variation.status === 'Approved').forEach((variation) => {
    const step = decisionStep(variation);
    const date = text(step?.date).slice(0, 10) || text(variation.updated_at).slice(0, 10) || text(variation.created_at).slice(0, 10);
    if (!date) return;
    list.push({ id: `vo-${variation.id}`, date, label: text(variation.vo_number) || 'VO', tone: 'violet', mono: true, kind: 'variation', approx: !step });
  });
  if (retention && handover) {
    list.push({ id: 'retention', date: handover, label: `Retention ${formatNumber(retention.amount, { decimals: 0 })}`, tone: 'gray', kind: 'retention' });
  }
  return {
    milestones: list.sort((a, b) => a.date.localeCompare(b.date)),
    invoiceCount: list.filter((entry) => entry.kind === 'invoice').length,
    voCount: list.filter((entry) => entry.kind === 'variation').length,
    hasRetention: Boolean(retention),
  };
}

/**
 * 3-week lookahead (#7): what is planned between `from` and `from + days - 1`: [{ kind, label, start, end, status,
 * owner, proposed, note }], by start date. `items` are timeline items (saved and proposed).
 */
export function lookaheadRows(items = [], { from, days = 21 } = {}) {
  const first = toDay(from);
  if (first === null) return { rows: [], from: null, to: null };
  const last = first + days - 1;
  const rows = items
    .filter((item) => {
      const a = toDay(item.start);
      const b = toDay(item.end || item.start);
      return a !== null && b !== null && a <= last && b >= first;
    })
    .sort((a, b) => a.start.localeCompare(b.start) || a.label.localeCompare(b.label));
  return { rows, from: dayToIso(first), to: dayToIso(last) };
}

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/** A print-ready page (the browser's "Save as PDF") for the consultant and the contractor. */
export function lookaheadHtml({ project = {}, rows = [], from, to, generatedBy = '', generatedAt = new Date() } = {}) {
  const body = rows.length ? rows.map((row) => `
      <tr>
        <td>${escapeHtml(row.kind === 'stage' ? 'Stage' : row.kind === 'milestone' ? 'Milestone' : 'Execution')}</td>
        <td><b>${escapeHtml(row.label)}</b>${row.note ? `<div class="muted">${escapeHtml(row.note)}</div>` : ''}</td>
        <td>${escapeHtml(formatDate(row.start))}</td>
        <td>${escapeHtml(formatDate(row.end || row.start))}</td>
        <td>${escapeHtml(row.status || '')}${row.proposed ? ' <span class="tag">proposed</span>' : ''}</td>
        <td>${escapeHtml(row.owner || '')}</td>
      </tr>`).join('') : '<tr><td colspan="6" class="muted">Nothing is planned in these three weeks.</td></tr>';
  const parties = [
    project.consultant ? `Consultant: ${project.consultant}` : '',
    project.contractor ? `Contractor: ${project.contractor}` : '',
    project.project_engineer ? `PE: ${project.project_engineer}` : '',
  ].filter(Boolean).join(' · ');
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(project.name || 'Project')} — 3-week lookahead ${escapeHtml(formatDate(from))}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #14163a; margin: 28px; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .sub { color: #555874; font-size: 12.5px; margin: 0 0 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  th { text-align: left; background: #f3f4f9; color: #555874; font-size: 11.5px; text-transform: uppercase; letter-spacing: .04em; }
  th, td { border-bottom: 1px solid #e3e5ef; padding: 7px 8px; vertical-align: top; }
  .muted { color: #686a8e; font-size: 11.5px; }
  .tag { border: 1px dashed #8b8fc0; border-radius: 4px; padding: 0 4px; font-size: 10.5px; color: #3b3f8f; }
  footer { margin-top: 18px; color: #686a8e; font-size: 11px; }
  @media print { body { margin: 12mm; } }
</style></head>
<body>
  <h1>${escapeHtml(project.name || 'Project')} — 3-week lookahead</h1>
  <p class="sub">${escapeHtml(formatDate(from))} – ${escapeHtml(formatDate(to))}${parties ? ` · ${escapeHtml(parties)}` : ''}${project.end_date ? ` · Planned handover ${escapeHtml(formatDate(project.end_date))}` : ''}</p>
  <table>
    <thead><tr><th>Type</th><th>Item</th><th>Start</th><th>End</th><th>Status</th><th>Responsible</th></tr></thead>
    <tbody>${body}</tbody>
  </table>
  <footer>Generated ${escapeHtml(formatDate(generatedAt))}${generatedBy ? ` by ${escapeHtml(generatedBy)}` : ''} · Sara PM</footer>
  <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 250); });</script>
</body></html>`;
}
